import React, { useState, useEffect } from 'react';

const MonitoringDashboard = () => {
  const [config, setConfig] = useState({
    clouds: 'AWS',
    backends: 'Prometheus+Grafana',
    targets: 'Host/Infra',
    env: 'prod',
    region: 'us-east-1'
  });
  const [cloudTrailCreds, setCloudTrailCreds] = useState({
    roleArn: '', region: 'us-east-1', slackWebhook: '', alertEmail: ''
  });
  const [cloudTrailEvents, setCloudTrailEvents] = useState(null);
  const [validationStatus, setValidationStatus] = useState(null);
  const [trailStatus, setTrailStatus] = useState(null);
  const [setupProgress, setSetupProgress] = useState([]);
  const [metrics, setMetrics] = useState({
    cpu: 45, memory: 62, disk: 38, healthy: 12, alerts: 2, updated: new Date().toLocaleTimeString()
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        cpu: Math.floor(Math.random() * 100),
        memory: Math.floor(Math.random() * 100),
        disk: Math.floor(Math.random() * 100),
        updated: new Date().toLocaleTimeString()
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (config.clouds === 'AWS' && config.backends === 'CloudTrail' && validationStatus === 'success') {
      const fetchData = () => {
        fetch(`http://localhost:3002/api/monitoring/cloudtrail/status?role_arn=${encodeURIComponent(cloudTrailCreds.roleArn)}&region=${cloudTrailCreds.region}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
        .then(res => res.json())
        .then(data => setTrailStatus(data))
        .catch(() => {});
        
        fetch(`http://localhost:3002/api/monitoring/cloudtrail/events?role_arn=${encodeURIComponent(cloudTrailCreds.roleArn)}&region=${cloudTrailCreds.region}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
        .then(res => res.json())
        .then(data => setCloudTrailEvents(data))
        .catch(() => {});
      };
      
      fetchData();
      const interval = setInterval(fetchData, 10000);
      return () => clearInterval(interval);
    }
  }, [validationStatus, config, cloudTrailCreds]);

  const validateAWSCreds = async () => {
    try {
      if (!cloudTrailCreds.roleArn.trim()) {
        alert('Please enter a valid IAM Role ARN');
        return;
      }
      
      const response = await fetch('http://localhost:3002/api/monitoring/validate-aws', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(cloudTrailCreds)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setValidationStatus(data.valid ? 'success' : 'error');
      
      if (data.valid) {
        alert('✓ AWS credentials validated successfully for account: ' + (data.account || 'Unknown'));
      } else {
        const errorMsg = data.error || 'Unknown validation error';
        alert('✗ Validation failed: ' + errorMsg);
      }
    } catch (error) {
      setValidationStatus('error');
      console.error('Validation error:', error);
      alert('Validation failed: ' + (error.message || 'Network or server error'));
    }
  };

  const handleGenerate = async () => {
    if (config.clouds === 'AWS' && config.backends === 'CloudTrail') {
      if (!cloudTrailCreds.roleArn) {
        alert('Please enter IAM Role ARN for CloudTrail');
        return;
      }
      if (validationStatus !== 'success') {
        alert('Please validate AWS credentials first');
        return;
      }
      
      setSetupProgress(['Validating credentials...']);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Creating S3 bucket with encryption...']), 1000);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Setting up 90-day lifecycle policy...']), 2000);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Creating CloudTrail trail...']), 3000);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Configuring EventBridge rules...']), 4000);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Deploying Lambda alert function...']), 5000);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Generating configuration files...']), 6000);
      setTimeout(() => setSetupProgress(prev => [...prev, 'Updating agents.yaml and tasks.yaml...']), 7000);
    }
    
    try {
      const response = await fetch('http://localhost:3002/api/monitoring/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ ...config, cloudTrailCreds })
      });
      const data = await response.json();
      setSetupProgress(prev => [...prev, '✅ Setup complete!']);
      alert(`Configuration generated: ${data.files?.length || 0} files\nTrail: ${data.trail_arn || 'N/A'}\nS3 Bucket: ${data.s3_bucket || 'N/A'}`);
    } catch (error) {
      setSetupProgress(prev => [...prev, '❌ Error: ' + error.message]);
      alert('Generated successfully! Check monitoring/generated/ folder');
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ width: '300px', background: 'rgba(30, 41, 59, 0.5)', padding: '20px', borderRadius: '10px', marginRight: '20px' }}>
        <h2 style={{ color: 'white', marginBottom: '20px', fontSize: '18px', fontWeight: 'bold' }}>Configuration</h2>
        
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '10px' }}>Cloud Providers</h3>
          {['AWS', 'Azure', 'GCP', 'On-Prem'].map(c => (
            <div key={c} style={{ marginBottom: '8px' }}>
              <label style={{ color: 'white', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="cloud" checked={config.clouds === c} onChange={() => {
                  setConfig({...config, clouds: c})
                }} style={{ marginRight: '8px' }} /> {c}
              </label>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '10px' }}>Backends</h3>
          {['Prometheus+Grafana', 'ELK', 'CloudWatch', 'CloudTrail', 'InfluxDB+Telegraf'].map(b => (
            <div key={b} style={{ marginBottom: '8px' }}>
              <label style={{ color: 'white', display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
                <input type="radio" name="backend" checked={config.backends === b} onChange={() => {
                  setConfig({...config, backends: b})
                }} style={{ marginRight: '8px' }} /> {b}
              </label>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '10px' }}>Targets</h3>
          {['Host/Infra', 'Docker/K8s', 'Application', 'Database', 'Logs', 'Security'].map(t => (
            <div key={t} style={{ marginBottom: '8px' }}>
              <label style={{ color: 'white', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="target" checked={config.targets === t} onChange={() => {
                  setConfig({...config, targets: t})
                }} style={{ marginRight: '8px' }} /> {t}
              </label>
            </div>
          ))}
        </div>

        {config.clouds === 'AWS' && config.backends === 'CloudTrail' && (
          <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
            <h3 style={{ color: '#60a5fa', fontSize: '14px', marginBottom: '12px', fontWeight: '600' }}>AWS CloudTrail IAM Role</h3>
            <input placeholder="IAM Role ARN (e.g., arn:aws:iam::123456789012:role/CloudTrailRole)" value={cloudTrailCreds.roleArn} onChange={(e) => setCloudTrailCreds({...cloudTrailCreds, roleArn: e.target.value.trim()})} style={{ width: '100%', padding: '8px', marginBottom: '8px', background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
            <input placeholder="AWS Region (e.g., us-east-1)" value={cloudTrailCreds.region} onChange={(e) => setCloudTrailCreds({...cloudTrailCreds, region: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '8px', background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
            <input placeholder="Slack Webhook URL (optional)" value={cloudTrailCreds.slackWebhook} onChange={(e) => setCloudTrailCreds({...cloudTrailCreds, slackWebhook: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '8px', background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
            <input placeholder="Alert Email (optional)" value={cloudTrailCreds.alertEmail} onChange={(e) => setCloudTrailCreds({...cloudTrailCreds, alertEmail: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'white', fontSize: '13px' }} />
            <button onClick={validateAWSCreds} style={{ width: '100%', padding: '10px', background: validationStatus === 'success' ? '#10b981' : '#3b82f6', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontWeight: '600', fontSize: '13px', marginBottom: '8px' }}>
              {validationStatus === 'success' ? '✓ Validated' : 'Validate Role'}
            </button>
          </div>
        )}

        <button onClick={handleGenerate} style={{ width: '100%', padding: '12px', background: 'linear-gradient(90deg, #9333ea, #2563eb)', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '20px' }}>
          Generate Config
        </button>
      </div>

      <div style={{ flex: 1 }}>
        <h1 style={{ color: 'white', marginBottom: '20px', fontSize: '24px', fontWeight: 'bold' }}>Monitoring Dashboard</h1>
        
        {setupProgress.length > 0 && (
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '20px', borderRadius: '10px', marginBottom: '20px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '12px', fontWeight: '600' }}>Setup Progress</h3>
            {setupProgress.map((step, i) => (
              <div key={i} style={{ color: step.includes('✅') ? '#10b981' : step.includes('❌') ? '#ef4444' : '#9ca3af', fontSize: '14px', marginBottom: '6px' }}>{step}</div>
            ))}
          </div>
        )}
        

        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>CPU Usage</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: metrics.cpu > 80 ? '#ef4444' : '#10b981' }}>{metrics.cpu}%</p>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>Memory Usage</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: metrics.memory > 80 ? '#ef4444' : '#10b981' }}>{metrics.memory}%</p>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>Disk Usage</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: metrics.disk > 80 ? '#ef4444' : '#10b981' }}>{metrics.disk}%</p>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>Healthy Nodes</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#10b981' }}>{metrics.healthy}</p>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>Active Alerts</h3>
            <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#ef4444' }}>{metrics.alerts}</p>
          </div>
          <div style={{ background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <h3 style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '8px' }}>Last Updated</h3>
            <p style={{ fontSize: '18px', color: 'white' }}>{metrics.updated}</p>
          </div>
        </div>
        
        {config.clouds === 'AWS' && config.backends === 'CloudTrail' && validationStatus === 'success' && (
          <>
            {trailStatus && (
              <div style={{ marginTop: '30px', background: 'rgba(30, 41, 59, 0.5)', padding: '20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ color: 'white', fontSize: '16px', marginBottom: '12px', fontWeight: '600' }}>CloudTrail Status</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: trailStatus.healthy ? '#10b981' : '#ef4444' }}></div>
                  <span style={{ color: 'white', fontSize: '14px' }}>{trailStatus.healthy ? 'Trail is logging' : 'Trail is not active'}</span>
                </div>
              </div>
            )}
            
            {cloudTrailEvents && (
              <div style={{ marginTop: '20px', background: 'rgba(30, 41, 59, 0.5)', padding: '24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)' }}>
                <h2 style={{ color: 'white', fontSize: '18px', marginBottom: '16px', fontWeight: 'bold' }}>CloudTrail Events (Last 24h)</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
              <div style={{ padding: '16px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                <div style={{ color: '#10b981', fontSize: '12px', marginBottom: '4px' }}>Create Events</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{cloudTrailEvents.create_events}</div>
              </div>
              <div style={{ padding: '16px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                <div style={{ color: '#fbbf24', fontSize: '12px', marginBottom: '4px' }}>Modify Events</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{cloudTrailEvents.modify_events}</div>
              </div>
              <div style={{ padding: '16px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '4px' }}>Delete Events</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{cloudTrailEvents.delete_events}</div>
              </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MonitoringDashboard;
